// Svelte 5 no longer needs svelte/store imports for basic reactivity
import { environment } from "./envStore.svelte.js";
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";

function getDefaultBackgroundColor(value) {
    return value === "transparent" ? "transparent" : "black";
}

// Load saved display defaults from localStorage
function loadDisplayDefaults() {
    if (typeof localStorage === 'undefined') return {};
    
    try {
        const displayDefaults = JSON.parse(localStorage.getItem("cc-display-defaults")) || {};
        
        return {
            width: displayDefaults.width ?? 32,
            height: displayDefaults.height ?? 15,
            xPadding: displayDefaults.xPadding ?? 10,
            yPadding: displayDefaults.yPadding ?? 10,
            lineSpacing: displayDefaults.lineSpacing ?? 1,
            color : displayDefaults.color ?? "#ffffff",            
            outlineColor : displayDefaults.outlineColor ?? "#000000",
            adrFontSize: displayDefaults.adrFontSize ?? 16,
            defaultBackgroundColor: getDefaultBackgroundColor(displayDefaults.defaultBackgroundColor)
        };
        
    } catch (error) {
        console.warn("Failed to load display defaults from localStorage:", error);
        return {};
    }
}

// Load defaults on initialization
const savedDisplayDefaults = loadDisplayDefaults();

// Create reactive state for styling
export const styleState = $state({    
    name: "Default",
    ccMode : true,
    borderStyle: "3", // 1 or 3:  1=Outline + drop shadow, 3=Opaque box
    fontFamily: "plexMono",
    fontSize: 16,
    spacing: 0,    
    opacity: 100,
    shadow: 0,
    shadowColor: "#000000",
    shadowOpacity: 100,
    outline: 5,        
    outlineOpacity: 100,    
    letterSpacing : 1,
    adrPreview: false,
    adrFontSize: savedDisplayDefaults.adrFontSize || 22,
    /* Display options */
    color: savedDisplayDefaults.color || "#ffffff",
    outlineColor: savedDisplayDefaults.outlineColor || "#000000",    
    height: savedDisplayDefaults.height ?? 15, //line count
    width: savedDisplayDefaults.width ?? 32, //character count
    xPadding: savedDisplayDefaults.xPadding ?? 10, //%
    yPadding: savedDisplayDefaults.yPadding ?? 10, //%
    lineSpacing: savedDisplayDefaults.lineSpacing ?? 1, //%
    defaultBackgroundColor: savedDisplayDefaults.defaultBackgroundColor ?? "black",
    presets : [],
    selectedPreset : false
});

// Function to select a preset and apply its values
export function selectPreset() {
    styleState.selectedPreset = styleState.selectedPreset ? styleState.selectedPreset : styleState.presets[0];
    if (styleState.selectedPreset) {
        // Loop over the keys in the selected preset and update the styleState
        for (let key in styleState.selectedPreset) {
            if (key !== "id" && key !== "name" && styleState.hasOwnProperty(key) && key !== "lineSpacing") {
                styleState[key] = styleState.selectedPreset[key];
            }
        }
    }
}

// Initialize presets - load from Firebase or localStorage
export async function initializePresets() {
    if (environment.online) {
        try {
            const userId = firebase.auth().currentUser?.uid;
            if (!userId) {
                console.warn("No user authenticated, skipping preset loading");
                return;
            }

            const homeRef = db.collection("users").doc(userId).collection("styles");
            const querySnapshot = await homeRef.orderBy("name").get();
            
            styleState.presets = [];
            querySnapshot.forEach((doc) => {
                styleState.presets = [...styleState.presets, doc.data()];
            });

            styleState.selectedPreset = styleState.presets[0] || false;
            selectPreset();
        } catch (error) {
            console.error("Error loading presets from Firebase:", error);
        }
    } else {
        try {
            const offlinePresets = localStorage.getItem("cc-style-styleState.presets");
            styleState.presets = offlinePresets ? JSON.parse(offlinePresets) : [];

            styleState.selectedPreset = styleState.presets[0] || false;
            selectPreset();
        } catch (error) {
            console.error("Error loading presets from localStorage:", error);
        }
    }
}

// Helper function to get preset ID by name
function getPresetIdByName(name) {
    const preset = styleState.presets.find((pre) => pre.name === name);
    return preset ? preset.id : false;
}

// Helper function to create preset info from current style state
function createPresetInfo(name, id) {
    return {
        id,
        name,
        borderStyle: styleState.borderStyle,
        fontFamily: styleState.fontFamily,
        fontSize: styleState.fontSize,
        spacing: styleState.spacing,
        opacity: styleState.opacity,
        shadow: styleState.shadow,
        shadowColor: styleState.shadowColor,
        shadowOpacity: styleState.shadowOpacity,
        outline: styleState.outline,
        outlineOpacity: styleState.outlineOpacity,
        letterSpacing: styleState.letterSpacing,
        adrPreview: styleState.adrPreview,
        adrFontSize: styleState.adrFontSize,
        color: styleState.color,
        outlineColor: styleState.outlineColor,
        height: styleState.height,
        width: styleState.width,
        xPadding: styleState.xPadding,
        yPadding: styleState.yPadding
    };
}

// Save a preset (create or update)
export async function savePreset(presetName, uuidGenerator) {
    if (!presetName) {
        throw new Error("Preset name is required");
    }

    const presetId = getPresetIdByName(presetName) || uuidGenerator();
    const presetInfo = createPresetInfo(presetName, presetId);

    const presetIndex = styleState.presets.findIndex((pre) => pre.id === presetId);

    if (environment.online) {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) {
            throw new Error("No user authenticated");
        }

        const homeRef = db.collection("users").doc(userId).collection("styles");
        
        await homeRef.doc(presetId).set(presetInfo);
        
        if (presetIndex > -1) {
            styleState.presets[presetIndex] = presetInfo;
        } else {
            styleState.presets.push(presetInfo);
        }
    } else {
        if (presetIndex > -1) {
            styleState.presets[presetIndex] = presetInfo;
        } else {
            styleState.presets.push(presetInfo);
        }

        localStorage.setItem(
            "cc-style-styleState.presets",
            JSON.stringify(styleState.presets)
        );
    }

    styleState.selectedPreset = presetIndex > -1 
        ? styleState.presets[presetIndex] 
        : styleState.presets[styleState.presets.length - 1];
    
    selectPreset();
}

// Delete a preset
export async function deletePreset() {
    if (!styleState.selectedPreset) {
        throw new Error("No preset selected");
    }

    const presetId = styleState.selectedPreset.id;
    
    styleState.presets = styleState.presets.filter((pre) => pre.id !== presetId);
    styleState.selectedPreset = styleState.presets.length > 0 ? styleState.presets[0] : false;

    if (environment.online) {
        const userId = firebase.auth().currentUser?.uid;
        if (!userId) {
            throw new Error("No user authenticated");
        }

        const homeRef = db.collection("users").doc(userId).collection("styles");
        await homeRef.doc(presetId).delete();
    } else {
        localStorage.setItem(
            "cc-style-styleState.presets",
            JSON.stringify(styleState.presets)
        );
    }

    selectPreset();
}

// Initialize presets immediately when the store loads
initializePresets();